import { useEffect, useState } from "react";
import { Field, ForAudience, FormSection, Menu } from "@omnabase/ui";
import { accessName, defaultOn, seesProject, type Project, type ProjectInbox, type WorkspaceMember } from "@omnabase/shared";
import { api } from "../../lib/api";
import { useCan } from "../../lib/access";
import { useSession } from "../../shell/store";
import { RuleSelect } from "./RuleSelect";
import { rolesOf } from "./team";

/** A project's People tab (2026-10-17, DECISIONS "Who receives a project's tickets: the Inbox column on the project's People tab"): the
 *  Everyone rule as before, then the kit's table of the people who see the project, Person, Role, Inbox, and below it those the rules keep
 *  out (Role only, so they can be let in from here). Role and Inbox are the team right's (the member route needs it); without it the rows
 *  are read-only, the Everyone rule stays the project administrator's. The owner's Role is fixed, the owner's Inbox is not. */

type Choice = ProjectInbox[string];
type Person = WorkspaceMember;
const INBOX_CHOICES: { id: Choice; label: string }[] = [{ id: "all", label: "All tickets" }, { id: "assigned", label: "Only when assigned" }];
export const inboxLabel = (c: Choice | undefined) => (c === "assigned" ? "Only when assigned" : "All tickets");

export function ProjectPeople({ project: p }: { project: Project }) {
  const { workspace, reload, toast } = useSession();
  const editable = useCan()("team");
  const roles = rolesOf(workspace);
  const ref = { id: p.id, everyone: p.everyoneRole };
  const active = workspace.members.filter((m) => m.status !== "deactivated");
  const save = async (path: string, body: Record<string, unknown>) => { try { await api.patch(`/api/v1/w/${workspace.slug}/${path}`, body); } catch (e) { toast((e as Error).message, { kind: "error" }); } await reload(); }; // a refusal reads back what is stored
  const setRule = (m: Person, rule: string | null) => { const next = { ...(m.projectRoles ?? {}) }; if (rule === null) delete next[p.id]; else next[p.id] = rule; void save(`members/${m.id}`, { projectRoles: Object.keys(next).length ? next : null }); };
  const setInbox = (m: Person, choice: Choice) => void save(`members/${m.id}`, { projectInbox: inboxAfter(m, p.id, choice, workspace.projects) });
  const row = { project: p, roles, editable, onRule: setRule, onInbox: setInbox };
  return (
    <>
      <FormSection title="Default role" audience="locked" intro="Applies to everyone you don't set individually below."
        toggle={<RuleSelect label="Everyone on the team" roles={roles} rule={p.everyoneRole ?? undefined} defaultLabel="Each person's default role" onChange={(rule) => void save(`projects/${p.id}`, { everyoneRole: rule })} />}>{null}</FormSection>
      <FormSection title="Team members" intro="Role: what they can do. Queue: what of this project lands in the person's queue. Team views always show every ticket.">
        <PeopleTable {...row} people={active.filter((m) => seesProject(m, ref))} />
        <NoAccessTable {...row} people={active.filter((m) => !seesProject(m, ref))} />
      </FormSection>
    </>
  );
}

/** The person's whole Inbox map after one choice on this project: "All tickets" leaves the project out; only projects the person still sees
 *  are sent (the server refuses an Inbox for a project the person cannot see) */
function inboxAfter(m: Person, projectId: string, choice: Choice, projects: readonly Project[]): ProjectInbox {
  const seen = new Set(projects.filter((x) => seesProject(m, { id: x.id, everyone: x.everyoneRole })).map((x) => x.id));
  const next: ProjectInbox = Object.fromEntries(Object.entries(m.projectInbox ?? {}).filter(([id]) => seen.has(id)));
  if (choice === "all") delete next[projectId]; else next[projectId] = choice;
  return next;
}

type RowProps = { project: Project; roles: ReturnType<typeof rolesOf>; editable: boolean; onRule: (m: Person, rule: string | null) => void; onInbox: (m: Person, choice: Choice) => void };

/** the people who see the project: the kit's table, Person, Role, Queue (renamed from Inbox in the queue-and-chat-column round) */
function PeopleTable({ people, project: p, roles, editable, onRule, onInbox }: RowProps & { people: Person[] }) {
  return (
    <div className="ob-dtable-card">
      <table className="ob-dtable ob-project-people" aria-label={`People on ${p.name}`}>
        <thead><tr><th>Person</th><th>Role</th><th>Queue</th></tr></thead>
        <tbody>
          {people.map((m) => (
            <tr key={m.id} className="ob-dtable__row">
              <td className="ob-dtable__grow"><PersonCell m={m} project={p} /></td>
              <td><RoleCell m={m} project={p} roles={roles} editable={editable} onRule={onRule} /></td>
              <td>{editable ? <InboxSelect label={`Queue for ${m.name} on ${p.name}`} value={m.projectInbox?.[p.id]} onChange={(c) => onInbox(m, c)} /> : <span className="ob-project-people__value">{inboxLabel(m.projectInbox?.[p.id])}</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** the people the rules keep out of the project: no Inbox (nothing reaches them), the Role so an administrator can let them in */
function NoAccessTable({ people, project: p, roles, editable, onRule }: RowProps & { people: Person[] }) {
  if (!people.length) return null;
  return (
    <div className="ob-dtable-card">
      <table className="ob-dtable ob-project-people ob-project-people--off" aria-label={`No access to ${p.name}`}>
        <thead><tr><th>No access</th><th>Role</th></tr></thead>
        <tbody>
          {people.map((m) => (
            <tr key={m.id} className="ob-dtable__row ob-dtable__row--dim">
              <td className="ob-dtable__grow"><PersonCell m={m} project={p} /></td>
              <td><RoleCell m={m} project={p} roles={roles} editable={editable} onRule={onRule} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** the avatar, the name and where the person's access comes from; both lines cut with "…", whole on hover (2026-10-17) */
function PersonCell({ m, project: p }: { m: Person; project: Project }) {
  const first = m.name.split(" ")[0];
  const note = m.role === "owner" ? "Owner, can't be narrowed" : m.projectRoles?.[p.id] ? `Set for ${first} on this project` : p.everyoneRole ? "Default: what everyone gets here" : "Uses the default role";
  return <span className="ob-dtable__person"><span className="ob-avatar ob-avatar--sm">{m.initials}</span><span className="ob-dtable__who"><span className="ob-dtable__name" title={m.name}>{m.name}</span><span className="ob-dtable__email" title={note}>{note}</span></span></span>;
}

/** the access rule as today's select; the owner's is fixed, and without the team right every one is */
function RoleCell({ m, project: p, roles, editable, onRule }: Pick<RowProps, "roles" | "editable" | "onRule"> & { m: Person; project: Project }) {
  const ref = { id: p.id, everyone: p.everyoneRole };
  const own = m.projectRoles?.[p.id];
  return <ForAudience audience="locked"><RuleSelect label={`${m.name}'s role on ${p.name}`} roles={roles} rule={m.role === "owner" ? "owner" : own} defaultLabel={`Default · ${accessName(roles, defaultOn(m, ref))}`} disabled={m.role === "owner" || !editable} onChange={(rule) => onRule(m, rule)} /></ForAudience>;
}

/** the Inbox as a chip with its menu, as the Role beside it; the choice shows at once, the workspace's next read confirms it (or puts it back) */
function InboxSelect({ label, value, onChange }: { label: string; value: Choice | undefined; onChange: (c: Choice) => void }) {
  const [shown, setShown] = useState<Choice>(value ?? "all");
  useEffect(() => setShown(value ?? "all"), [value]);
  return (
    <Menu align="end" trigger={<Field label={label} value={inboxLabel(shown)} />} sections={[
      { items: INBOX_CHOICES.map((c) => ({ id: c.id, label: c.label, checked: shown === c.id, onSelect: () => { setShown(c.id); onChange(c.id); } })) },
    ]} />
  );
}
